import { useSession } from '@/auth/useSession';
import { useTimeZone } from '@/auth/useTimeZone';
import { LoadingPage } from '@/components/loading-page';
import { Button } from '@/components/ui/button';
import { EmptyArray, getMyStudentSessions } from '@/lib/api-calls';
import { isFamilyStudent } from '@airreading/common';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Temporal } from 'temporal-polyfill';
import { FAMILY_INSTRUCTIONAL_CALENDAR_URL, FAMILY_SCHEDULING_ADMIN_EMAIL } from './family-student-portal';
import { SessionSection } from './session-section';
import { useErrorLogger } from '@/hooks/use-error-logger';

export const StudentHome = () => {
  const { actingUser } = useSession();
  const { timeZone } = useTimeZone();
  const logError = useErrorLogger();
  const familyStudent = actingUser ? isFamilyStudent(actingUser) : false;

  const todayDate = Temporal.Now.zonedDateTimeISO(timeZone).toPlainDate();

  const dayStartInstant = (d: Temporal.PlainDate, tz: string) =>
    d.toZonedDateTime({ timeZone: tz, plainTime: Temporal.PlainTime.from('00:00') }).toInstant();

  const startOfToday = dayStartInstant(todayDate, timeZone);
  const startOfTomorrow = dayStartInstant(todayDate.add({ days: 1 }), timeZone);

  const { data, isPending, error } = useQuery(
    getMyStudentSessions({
      pageIndex: 0,
      pageSize: 20,
      startOnOrAfter: startOfToday.toString(),
    }),
  );

  useEffect(() => {
    if (error) {
      logError('student session view fetch failed', {
        component: 'StudentHome',
        action: 'fetch_failed',
        userId: actingUser?.id,
      });
    }
  }, [error, logError, actingUser?.id]);

  const sessions = data?.studentSessions ?? EmptyArray;

  const toLocalDate = (inst: Temporal.Instant) => inst.toZonedDateTimeISO(timeZone).toPlainDate();
  const sorted = [...sessions].sort((a, b) => Temporal.Instant.compare(a.startInstant, b.startInstant));

  // Upcoming = all sessions happening today (00:00–23:59 local)
  const sessionsToday = sorted.filter(
    (s) =>
      Temporal.Instant.compare(s.startInstant, startOfToday) >= 0 &&
      Temporal.Instant.compare(s.startInstant, startOfTomorrow) < 0,
  );

  let upcoming = sessionsToday;

  // If none today, take all sessions on the next day that has sessions
  if (upcoming.length === 0) {
    const firstAfterToday = sorted.find((s) => Temporal.Instant.compare(s.startInstant, startOfTomorrow) >= 0);
    upcoming = firstAfterToday
      ? sorted.filter((s) => toLocalDate(s.startInstant).equals(toLocalDate(firstAfterToday.startInstant)))
      : [];
  }

  // Future = everything from start of today forward (includes Upcoming)
  const futures = sorted.filter((s) => Temporal.Instant.compare(s.startInstant, startOfToday) >= 0);

  const prevUpcomingLen = useRef<number>(upcoming.length);
  const prevFuturesLen = useRef<number>(futures.length);

  useEffect(() => {
    if (prevUpcomingLen.current > 0 && upcoming.length === 0) {
      logError('student session view narrowed', {
        component: 'StudentHome',
        action: 'upcoming_empty',
        userId: actingUser?.id,
      });
    }
    prevUpcomingLen.current = upcoming.length;
  }, [upcoming.length, logError, actingUser?.id]);

  useEffect(() => {
    if (prevFuturesLen.current > 0 && futures.length === 0) {
      logError('student session view narrowed', {
        component: 'StudentHome',
        action: 'futures_empty',
        userId: actingUser?.id,
      });
    }
    prevFuturesLen.current = futures.length;
  }, [futures.length, logError, actingUser?.id]);

  if (isPending) return <LoadingPage />;
  if (error) {
    return (
      <div className="p-4" role="alert">
        We are sorry, but we could not fetch your next class. Please notify your teacher about this issue.
      </div>
    );
  }

  return (
    <div className="block p-4 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex min-w-0 items-center">
          <span className="flex items-center font-bold text-2xl my-2 mr-3" aria-hidden="true">
            👋
          </span>
          <h2 className="min-w-0 break-words font-bold text-2xl my-2" data-testid="student-home-header-greeting">
            Hi {actingUser.firstName}!
          </h2>
        </div>
        {familyStudent && (
          <a href={FAMILY_INSTRUCTIONAL_CALENDAR_URL} target="_blank" rel="noreferrer noopener" className="shrink-0">
            <Button type="button" variant="outline" data-testid="student-home-instructional-calendar">
              Instructional calendar
            </Button>
          </a>
        )}
      </div>
      <SessionSection
        title="Upcoming classes"
        studentName={actingUser.fullNameOrEmail ?? ''}
        sessionsList={upcoming}
        data-testid="student-upcoming-classes-section"
      />
      {upcoming.length > 0 && (
        <SessionSection
          title="Future classes"
          studentName={actingUser.fullNameOrEmail ?? ''}
          sessionsList={futures}
          isFuture
          data-testid="student-future-classes-section"
        />
      )}
      {familyStudent && (
        <p className="mt-4 text-sm text-muted-foreground text-wrap" data-testid="student-home-scheduling-note">
          For any scheduling changes, please email{' '}
          <a
            href={`mailto:${FAMILY_SCHEDULING_ADMIN_EMAIL}`}
            className="text-air-primary hover:text-air-primary underline break-all rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            data-testid="student-home-link-scheduling-email"
          >
            {FAMILY_SCHEDULING_ADMIN_EMAIL}
          </a>
          .
        </p>
      )}
    </div>
  );
};
